import { gradeFor, shareText, RUN_URL } from './runEngine';

const HAND = '"Caveat", "Patrick Hand", "Baloo 2", cursive';
const DISPLAY = '"Baloo 2", Inter, system-ui, sans-serif';

const MODE_LABEL = { timed: 'Timed Run', endless: 'Endless Run' };

const INK = '#1f1633';
const RED = '#dc2626';
const W = 1080;
const H = 1500;
const SHEET_W = 940;
const SHEET_H = 1350;

function highlighter(g, x, y, w, h, color) {
  g.save();
  g.translate(x, y);
  g.rotate(-0.03);
  g.fillStyle = color;
  roundedRect(g, 0, 0, w, h, 14);
  g.fill();
  g.restore();
}

// ctx.roundRect is missing on Safari < 16, which many phones still run.
function roundedRect(g, x, y, w, h, r) {
  g.beginPath();
  g.moveTo(x + r, y);
  g.arcTo(x + w, y, x + w, y + h, r);
  g.arcTo(x + w, y + h, x, y + h, r);
  g.arcTo(x, y + h, x, y, r);
  g.arcTo(x, y, x + w, y, r);
  g.closePath();
}

function fitFont(g, text, weight, family, size, maxWidth) {
  let px = size;
  g.font = `${weight} ${px}px ${family}`;
  while (px > 40 && g.measureText(text).width > maxWidth) {
    px -= 4;
    g.font = `${weight} ${px}px ${family}`;
  }
  return px;
}

function drawNoto(g, noto, x, y, newBest) {
  if (newBest) {
    g.save();
    g.translate(x, y - 150);
    g.rotate(0.05);
    g.fillStyle = '#b45309';
    g.fillRect(-5, 20, 10, 90);
    g.font = `800 38px ${DISPLAY}`;
    const tw = g.measureText('NEW BEST!').width + 44;
    g.fillStyle = '#fff';
    g.strokeStyle = RED;
    g.lineWidth = 7;
    roundedRect(g, -tw / 2, -40, tw, 72, 16);
    g.fill();
    g.stroke();
    g.fillStyle = RED;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText('NEW BEST!', 0, -2);
    g.restore();
  }
  if (noto) g.drawImage(noto, x - 105, y - 110, 210, 260);
}

function drawStats(g, state, top, left, width) {
  const acc = state.total ? Math.round((state.correct / state.total) * 100) : 0;
  const stats = [
    [`${acc}%`, 'accuracy'],
    [String(state.bestStreak), 'best streak'],
    [String(state.stagesCleared ?? 0), 'stages'],
  ];
  const col = width / stats.length;
  g.textAlign = 'center';
  stats.forEach(([value, label], i) => {
    const cx = left + col * i + col / 2;
    g.fillStyle = INK;
    g.font = `800 84px ${DISPLAY}`;
    g.fillText(value, cx, top);
    g.fillStyle = '#6b7280';
    g.font = `700 46px ${HAND}`;
    g.fillText(label, cx, top + 66);
  });
  g.strokeStyle = 'rgba(31,22,51,0.15)';
  g.lineWidth = 3;
  for (let i = 1; i < stats.length; i++) {
    g.beginPath();
    g.moveTo(left + col * i, top - 50);
    g.lineTo(left + col * i, top + 80);
    g.stroke();
  }
}

// The shareable report card: score, grade and stats only, never the answers.
export function drawShareCard(state, { newBest, art = {} } = {}) {
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const g = c.getContext('2d');

  g.fillStyle = '#e8cfa4';
  g.fillRect(0, 0, W, H);
  g.save();
  g.translate(W / 2, H / 2);
  g.rotate(-0.012);
  const L = -SHEET_W / 2;
  const T = -SHEET_H / 2;
  g.fillStyle = 'rgba(0,0,0,0.16)';
  g.fillRect(L + 14, T + 18, SHEET_W, SHEET_H);
  g.fillStyle = '#fffdf6';
  g.fillRect(L, T, SHEET_W, SHEET_H);

  g.strokeStyle = 'rgba(96,144,200,0.28)';
  g.lineWidth = 3;
  for (let y = T + 250; y < T + SHEET_H; y += 64) {
    g.beginPath();
    g.moveTo(L, y);
    g.lineTo(L + SHEET_W, y);
    g.stroke();
  }
  g.strokeStyle = 'rgba(220,70,80,0.5)';
  g.beginPath();
  g.moveTo(L + 120, T + 180);
  g.lineTo(L + 120, T + SHEET_H);
  g.stroke();

  g.fillStyle = RED;
  g.fillRect(L, T, SHEET_W, 180);
  g.fillStyle = '#fff';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.font = `700 84px ${HAND}`;
  g.fillText('School of NameIt', 0, T + 76);
  g.font = `800 30px ${DISPLAY}`;
  g.fillText('N A M E I T   R U N   R E P O R T', 0, T + 140);

  const tag = MODE_LABEL[state.mode] || 'Run';
  g.font = `700 54px ${HAND}`;
  const tw = g.measureText(tag).width;
  highlighter(g, L + 160, T + 222, tw + 40, 66, 'rgba(216,180,254,0.8)');
  g.textAlign = 'left';
  g.fillStyle = '#374151';
  g.fillText(tag, L + 180, T + 257);

  const scoreY = T + 470;
  const grade = gradeFor(state);
  g.save();
  g.translate(L + 62, scoreY - 40);
  g.rotate(-0.12);
  g.strokeStyle = RED;
  g.lineWidth = 7;
  g.beginPath();
  g.ellipse(0, 0, 50, 44, 0, 0.2, Math.PI * 2 + 0.3);
  g.stroke();
  g.fillStyle = RED;
  g.textAlign = 'center';
  g.font = `700 ${grade.length > 1 ? 58 : 72}px ${HAND}`;
  g.fillText(grade, 2, 4);
  g.restore();

  const score = state.score.toLocaleString('en-US');
  g.textAlign = 'left';
  g.fillStyle = INK;
  fitFont(g, score, 800, DISPLAY, 210, 540);
  g.fillText(score, L + 160, scoreY);
  const sw = g.measureText(score).width;
  g.strokeStyle = '#ec4899';
  g.lineWidth = 14;
  g.lineCap = 'round';
  g.beginPath();
  g.moveTo(L + 170, scoreY + 110);
  g.bezierCurveTo(L + 170 + sw * 0.3, scoreY + 84, L + 170 + sw * 0.6, scoreY + 128, L + 160 + sw, scoreY + 96);
  g.stroke();

  drawNoto(g, art.noto, L + 800, scoreY, newBest);

  drawStats(g, state, T + 740, L + 130, SHEET_W - 160);

  g.textAlign = 'center';
  g.fillStyle = INK;
  fitFont(g, `Can you beat ${score}?`, 700, HAND, 96, SHEET_W - 180);
  g.fillText(`Can you beat ${score}?`, 60, T + 960);

  g.fillStyle = '#374151';
  g.font = `700 60px ${HAND}`;
  g.fillText('Play free, no download!', 60, T + 1090);

  const url = RUN_URL.replace(/^https?:\/\//, '');
  g.font = `800 40px ${DISPLAY}`;
  const pw = g.measureText(url).width + 80;
  g.fillStyle = RED;
  roundedRect(g, 60 - pw / 2, T + SHEET_H - 120, pw, 80, 40);
  g.fill();
  g.fillStyle = '#fff';
  g.fillText(url, 60, T + SHEET_H - 80);
  g.restore();
  return c;
}

async function fontsReady() {
  try {
    await Promise.all([document.fonts.load(`700 80px ${HAND}`), document.fonts.load(`800 80px ${DISPLAY}`)]);
  } catch {
    // Canvas falls back to system fonts.
  }
}

export async function buildShareCard(state, opts = {}) {
  await fontsReady();
  const canvas = drawShareCard(state, opts);
  const blob = await new Promise((r) => canvas.toBlob(r, 'image/png'));
  return {
    blob,
    file: blob && new File([blob], 'nameit-run.png', { type: 'image/png' }),
    url: blob && URL.createObjectURL(blob),
    text: shareText(state, opts),
  };
}

export async function shareCard({ file, text }) {
  try {
    if (file && navigator.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], text });
      return 'shared';
    }
    if (navigator.share) {
      await navigator.share({ text });
      return 'shared';
    }
    await navigator.clipboard.writeText(text);
    return 'copied';
  } catch (err) {
    if (err?.name === 'AbortError') return 'cancelled';
    try {
      await navigator.clipboard.writeText(text);
      return 'copied';
    } catch {
      return 'failed';
    }
  }
}

export function saveCard({ url }) {
  const a = document.createElement('a');
  a.href = url;
  a.download = 'nameit-run.png';
  document.body.appendChild(a);
  a.click();
  a.remove();
}
